import type { MessageKey, Translate } from '../../i18n'
import type { Tone } from '../../ui'

/**
 * Maps the API's status vocabulary to a presentation tone. Lives outside
 * StatusBadge on purpose: the badge is generic, and the API vocabulary belongs
 * to the screen that talks to that API. A second data source with different
 * status names gets its own map rather than editing the badge.
 */
// Keyed by the raw API string, not by TxStatus.
//
// TxStatus is the vocabulary a biller may FILTER by — SUCCESS and FAILED. The
// warehouse may still return a historical REVERSED row, and a Core update
// could introduce another state before this map is updated. Rendering that
// row correctly matters more than the type being narrow here, so the map
// stays wider than the filter and falls back rather than crashing.
const TONES: Record<string, Tone> = {
  SUCCESS: 'success',
  FAILED: 'danger',
  PENDING: 'warning',
  REVERSED: 'neutral',
}

export const statusTone = (status: string): Tone => TONES[status] ?? 'neutral'

/**
 * The status as a reader should see it, in their own language.
 *
 * The list and the detail panel printed the raw API value, so a French user
 * read "SUCCESS" (MPAY-32206) — the translations existed and only the filter
 * dropdown was using them.
 *
 * Falls back to the raw value for a status this map does not know, for the
 * same reason `statusTone` does: the warehouse can return a state the portal
 * has not been taught yet, and showing Core's own word for it beats showing a
 * missing translation key or an empty cell.
 */
export function statusLabel(t: Translate, status: string): string {
  const key = `tx.status.${status}` as MessageKey
  const translated = t(key)
  return translated === key ? status : translated
}
